import { useRef, useState } from "react";
import { UploadCloud, X, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, imgUrl } from "@/lib/api";

export default function ImageUploader({ value = [], onChange, max = 20 }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (files) => {
    const list = Array.from(files).filter((f) => {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} bir görsel değil`); return false; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} 8MB'den büyük`); return false; }
      return true;
    });
    if (value.length + list.length > max) {
      toast.error(`En fazla ${max} görsel yükleyebilirsiniz`);
      return;
    }
    const uploaded = [];
    setUploading(true);
    for (const f of list) {
      try {
        setProgress(0);
        const res = await uploadImage(f, setProgress);
        uploaded.push(res.url);
      } catch (e) {
        toast.error("Yükleme başarısız: " + (e.response?.data?.detail || f.name));
      }
    }
    setUploading(false);
    if (uploaded.length) onChange([...value, ...uploaded]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  const move = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const arr = [...value];
    const [it] = arr.splice(from, 1);
    arr.splice(to, 0, it);
    onChange(arr);
  };

  return (
    <div data-testid="image-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
          dragOver ? "border-primary bg-primary/5" : "border-zinc-300 hover:border-primary/50 hover:bg-zinc-50"
        }`}
      >
        {uploading ? (
          <>
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <p className="text-sm text-zinc-500">Yükleniyor... %{progress}</p>
          </>
        ) : (
          <>
            <UploadCloud className="h-8 w-8 text-zinc-400" />
            <p className="text-sm font-medium text-zinc-700">Görselleri buraya sürükleyin veya tıklayın</p>
            <p className="text-xs text-zinc-400">JPG, PNG, WEBP · Maks 8MB · {value.length}/{max}</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid="image-input"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {value.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5">
          {value.map((url, i) => (
            <div key={url + i} className="group relative aspect-square overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
              <img src={imgUrl(url)} alt="" className="h-full w-full object-cover" />
              {i === 0 && (
                <span className="absolute start-1 top-1 bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Kapak</span>
              )}
              <button
                type="button"
                data-testid={`remove-image-${i}`}
                onClick={() => removeAt(i)}
                className="absolute end-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50 px-1 py-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                <button type="button" onClick={() => move(i, i - 1)} className="text-[10px] text-white">◀</button>
                <GripVertical className="h-3 w-3 text-white/60" />
                <button type="button" onClick={() => move(i, i + 1)} className="text-[10px] text-white">▶</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
